/// <reference path="../../../../../typings/index.d.ts" />

module Meevo.Components.Controls {
    'use strict';

    import SecurityAccessEnum = Meevo.Model.SecurityAccessEnum;
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;

    interface ICategoryModel {
        id: string;
        displayName: string;
        selected: boolean;
        objectState: SecurityAccessEnum;
    }

    interface IEmployeeClassificationMoverController {
        // comes from directive's scope
        centralOffice: boolean;
        securityAccess: SecurityAccessEnum;
        hierarchyBusinessType: Guid | string;
        hierarchyId: string;
        hierarchyBranchItem: string;
        hierarchyLocationList: Array<any>;
        disabled: boolean;
        filterList?: any[];
        filterFn?: (item: Object, row: Object) => boolean
        isAllSelected?: any;

        // comes from directive's link function
        ngModel: angular.INgModelController;

        allCategoriesSelected: boolean;
        hierarchyCriteria: Object;
        canceller: ng.IDeferred<any>;
        fetching: boolean;
        error: string;
        securityDisabled: boolean;
        selectedFilterValue?: { label: string; value: string | number };

        initModelValue: any;
        all: ICategoryModel[];
        available: ICategoryModel[];
        selected: ICategoryModel[];
        availableSelection: string[];
        selectedSelection: string[];

        init(): void;
        setHierarchyWatchers(): void;
        createHierarchyCriteria(newVal: any, oldVal: any): void;
        fetchList(): void;
        populate(categories: any[]): void;
        setWatchers(): void;
        updateLists(): void;
        select(): void;
        deselect(): void;
        moveAll(selected: boolean): void;
        getList(selected: boolean): ICategoryModel[];
        resetModelViewValue(): void;
        checkAllCallback(): void;
    }

    export class EmployeeClassificationMoverController implements IEmployeeClassificationMoverController {
        static $inject = [
            '$scope',
            '$q',
            'meevo.components.common.IdentityService',
            'meevo.components.common.DataService'
        ];

        // comes from directive's scope
        centralOffice: boolean;
        securityAccess: SecurityAccessEnum;
        hierarchyBusinessType: Guid | string;
        hierarchyId: string;
        hierarchyBranchItem: string;
        hierarchyLocationList: Array<any>;
        disabled: boolean;
        filterList: Object[];
        filterFn: (item: Object, row: Object) => boolean;
        isAllSelected?: any;

        // comes from directive's link function
        ngModel: angular.INgModelController;

        public allCategoriesSelected: boolean;
        hierarchyCriteria: Object = {};
        canceller: ng.IDeferred<any> = null;
        fetching: boolean = false;
        error: string = null;
        securityDisabled: boolean = false;
        selectedFilter: Object;

        initModelValue = [];
        all: ICategoryModel[] = [];
        available: ICategoryModel[] = [];
        selected: ICategoryModel[] = [];
        availableSelection: string[] = [];
        selectedSelection: string[] = [];

        constructor(private $scope: angular.IScope,
                    private $q: angular.IQService,
                    private identityService: Meevo.Components.Common.IIdentityService,
                    private dataService: Meevo.Components.BaseClasses.IDataService) {
            {
                let value;
                Object.defineProperty(this, 'selectedFilter', {
                    get: () => value,
                    set: (v: any) => {
                        value = v;
                        if (this.ngModel) {
                            this.updateLists();
                        }
                    }
                })
            }
        }

        init(): void {
            this.hierarchyCriteria = {
                businessTypeList: [this.identityService.businessTypeId],
                locationList: [this.identityService.locationId],
                hierarchyId: null,
                branchId: null
            };

            this.filterList || (this.filterList = []);

            if (this.centralOffice) {
                this.setHierarchyWatchers();
            }

            this.setWatchers();

            if (this.isAllSelected != null)
                this.allCategoriesSelected = this.isAllSelected;
            else {
                this.allCategoriesSelected = true;
                this.isAllSelected = true;
            }

            if (this.securityAccess === SecurityAccessEnum.Grant) {
                if (this.allCategoriesSelected) {
                    this.resetModelViewValue();
                } else {
                    this.fetchList();
                }
            } else {
                this.securityDisabled = true;
                this.allCategoriesSelected = false;
                this.isAllSelected = false;

                this.fetching = true;
                this.dataService.getItem('Report/Employee', this.identityService.current().userId)
                    .then((data: any) => {
                        this.initModelValue = data.employeeCategories.map((item: ICategoryModel) => item.id);
                        this.populate(data.employeeCategories);
                        this.error = null;
                    })
                    .catch(() => {
                        this.error = 'Could not fetch employee categories list. Employee Classification Mover control can not be initialized.';
                    })
                    .finally(() => this.fetching = false);
            }
        }

        /**
         * Set watchers on Hierarchy control's variables in CO mode
         */
        setHierarchyWatchers(): void {
            this.$scope.$watch(() => this.hierarchyBusinessType, this.createHierarchyCriteria.bind(this));
            this.$scope.$watch(() => this.hierarchyBranchItem, this.createHierarchyCriteria.bind(this));
            this.$scope.$watch(() => this.hierarchyLocationList, this.createHierarchyCriteria.bind(this), true);
        }

        /**
         * Create hierarchy criteria for getting employee Categories
         * @param {any} newValue
         * @param {any} oldValue
         */
        createHierarchyCriteria(newValue, oldValue): void {
            if (!angular.isDefined(newValue) || angular.equals(newValue, oldValue)) return;

            if ((this.hierarchyId && !this.hierarchyBranchItem) || (!this.hierarchyId && this.hierarchyBranchItem)) return;

            this.hierarchyCriteria = {
                businessTypeList: this.hierarchyBusinessType,
                locationList: this.hierarchyLocationList,
                hierarchyId: this.hierarchyId,
                branchId: this.hierarchyBranchItem
            };

            if (this.securityAccess === SecurityAccessEnum.Grant) {
                if (this.allCategoriesSelected) {
                    this.resetModelViewValue();
                } else {
                    this.fetchList();
                }
            }

        }

        /**
         * Fetch categories list
         * */
        fetchList(): void {
            let criteria: Object;

            if (this.canceller) {
                this.canceller.resolve();
            }

            this.canceller = this.$q.defer();

            criteria = angular.extend(this.hierarchyCriteria, {
                objectStateEnumList: [ObjectStateEnum.Active],
                displayName: null,
                fieldOrderBy: 'DisplayName',
                orderAscDesc: 'Ascending'
            });

            this.fetching = true;
            this.dataService.getListPaged('Report/EmployeeCategory', {criteria}, null, null, {
                timeout: this.canceller.promise
            })
                .then((categories: ICategoryModel[] | any) => {
                    this.populate(categories);
                    this.error = null;
                })
                .catch(() => {
                    this.error = 'Could not fetch employee categories list. Employee Classification Mover control can not be initialized.';
                })
                .finally(() => {
                    this.fetching = false;
                });
        }

        /**
         * Process list of categories
         * @param {ICategoryModel[]} categories
         * */
        populate(categories: ICategoryModel[]): void {
            if (categories === null) {
                return;
            }

            this.all = categories
                .map((category): ICategoryModel => {
                    const {id, displayName, objectState} = category;
                    let selected = false;

                    if (this.securityAccess === SecurityAccessEnum.Grant) {
                        if (this.ngModel.$viewValue) {
                            selected = this.ngModel.$viewValue.some(cId => id === cId);
                        }
                    } else {
                        selected = true;
                    }

                    return {
                        id,
                        displayName,
                        selected,
                        objectState
                    }
                });
            this.updateLists();
        }

        /**
         * Set watchers
         * */
        setWatchers(): void {
        }

        /**
         * Update selected and available lists
         * */
        updateLists(): void {
            let value;

            this.selected = this.getList(true);
            this.available = this.getList(false);

            value = this.selected
                .map((c: ICategoryModel) => c.id);

            if (this.isAllSelected == false) {
                this.ngModel.$setViewValue(value);
            }
            else {
                this.resetModelViewValue(value);
            }
        }

        /**
         * Select category
         * */
        select(): void {
            this.available.forEach((c: ICategoryModel) => {
                if (this.availableSelection.indexOf(c.id) > -1) c.selected = true;
            });
            this.updateLists();
        }

        /**
         * Deselect category
         * */
        deselect(): void {
            this.selected.forEach((c: ICategoryModel) => {
                if (this.selectedSelection.indexOf(c.id) > -1) c.selected = false;
            });
            this.updateLists();
        }

        /**
         * Move all categories
         * @param {boolean} selected
         * */
        moveAll(selected: boolean): void {
            this.all.forEach((c: ICategoryModel) => {
                c.selected = selected;
            });
            this.updateLists();
        }

        /**
         * Get list by criteria
         * @param {boolean} selected
         * */
        getList(selected: boolean): ICategoryModel[] {
            return this.all
                .filter((c: ICategoryModel) => {
                    if (!(typeof this.filterFn === 'function')) {
                        return true;
                    }

                    return this.filterFn(this.selectedFilter, c)
                })
                .filter((c: ICategoryModel) => c.selected === selected);
        }

        /**
         * Callback for check/uncheck all categories
         * */
        checkAllCallback(): void {
            this.isAllSelected = this.allCategoriesSelected;
            if (this.allCategoriesSelected) {
                this.all = [];
                this.updateLists();
            } else {
                this.fetchList();
            }
        }

        /**
         * Reset model view value
         * @param {string[]} newValue
         * */
        resetModelViewValue(newValue: string[] = null) {
            if (Array.isArray(newValue) && (newValue.length === 0 || newValue.length === this.all.length) || newValue === null) {
                newValue = this.initModelValue;
            }

            if (this.ngModel)
                this.ngModel.$setViewValue(newValue);
        }
    }
}